/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  Lock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  MoreVertical,
  Layers,
  ChevronRight,
  ShieldAlert,
  ShieldCheck,
  Check,
  ChevronDown
} from 'lucide-react';
import { roleService } from '../services/roleService';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import toastService from '../utils/toastService';
import { usePermissions } from '../context/PermissionContext';
import { useNavigate } from 'react-router-dom';

const SEARCH_DEBOUNCE_MS = 500;

const Roles = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({
    search: '',
    scope: 'All',
    type: 'All'
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const scopeDropdownRef = React.useRef(null);

  // Debounce search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Outside click for scope dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scopeDropdownRef.current && !scopeDropdownRef.current.contains(event.target)) {
        setIsScopeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset to first page when search or scope changes
  useEffect(() => {
    setPagination(p => ({ ...p, page: 1 }));
  }, [debouncedSearch, filters.scope, filters.type]);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);

      // Clean query parameters to avoid passing empty or 'All' defaults
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (debouncedSearch) { params.search = debouncedSearch; }
      if (filters.scope && filters.scope !== 'All') { params.scope = filters.scope; }
      if (filters.type && filters.type !== 'All') { params.type = filters.type; }

      const response = await roleService.getRoles(params);
      const rolesData = response.data?.roles || (Array.isArray(response.data) ? response.data : []);

      setRoles(rolesData);

      const total = response.data.pagination?.total || response.data.total || rolesData.length;
      const limit = response.data.pagination?.limit || response.data.limit || pagination.limit;
      const totalPages = response.data.pagination?.totalPages || response.data.totalPages || Math.ceil(total / limit);

      setPagination({
        page: response.data.pagination?.page || response.data.page || pagination.page,
        limit,
        total,
        totalPages
      });
    } catch (err) {
      toastService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch, filters.scope, filters.type]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);



  const handleOpenDelete = (role) => {
    if (role.isSystem) {
      toastService.info('System roles cannot be deleted');
      return;
    }
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      await roleService.deleteRole(selectedRole.id);
      toastService.success('Role deleted');
      setShowDeleteModal(false);
      fetchRoles();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Deletion failed';
      toastService.error(errorMsg);
      setShowDeleteModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-y-auto p-2">

        {/* Header Section */}
        <div className=" border-b border-base-300 flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <h1 className="page-title flex items-center gap-3">
              Roles & Permission
            </h1>
          </div>
          {can.roles.create() && (
            <button
              onClick={() => navigate('/roles/new')}
              className="btn-premium"
            >
              <Plus size={18} />
              <span>Create New Role</span>
            </button>
          )}
        </div>

        {/* Filters and List Area */}
        <div className="pt-6 sm:pt-8  space-y-6">
          {/* Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between glass-panel !bg-base-content/5 p-4 rounded-2xl relative z-[60]">
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              <div className="relative flex-1 min-w-[280px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30" size={18} />
                <input
                  type="text"
                  placeholder="Search by role name or key..."
                  className="w-full pl-10 pr-4 py-2.5 bg-base-100 border border-base-content/10 rounded-xl text-sm focus:outline-none focus:border-primary/50 transition-all font-bold shadow-sm"
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                />
              </div>
              <div className="relative w-full sm:w-48" ref={scopeDropdownRef}>
                <button
                  onClick={() => setIsScopeOpen(!isScopeOpen)}
                  className="flex items-center justify-between gap-2 w-full rounded-xl px-4 py-2.5 bg-base-100 border border-base-content/10 text-sm font-bold focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none cursor-pointer group shadow-sm transition-all"
                >
                  <span className="truncate flex-1 text-left uppercase text-[10px] tracking-wider">
                    {filters.scope === 'All' ? 'All Scopes' : filters.scope.replace('_', ' ')}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isScopeOpen ? 'rotate-180' : ''}`} />
                </button>

                {isScopeOpen && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                    {['All', 'platform', 'account', 'sub_account'].map((scope) => (
                      <button
                        key={scope}
                        onClick={() => {
                          setFilters(f => ({ ...f, scope }));
                          setIsScopeOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${filters.scope === scope ? 'bg-primary/5' : ''}`}
                      >
                        <span className={`text-[11px] font-black uppercase tracking-widest ${filters.scope === scope ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>
                          {scope === 'All' ? 'All Scopes' : scope.replace('_', ' ')}
                        </span>
                        {filters.scope === scope && <Check size={12} className="text-primary ml-auto" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Table */}
          {loading && roles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 space-y-4">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Hydrating Roles</p>
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center py-32 glass-panel !bg-base-100/30 rounded-2xl border-dashed border-2 border-base-content/10">
              <div className="w-20 h-20 bg-base-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Shield size={40} className="text-base-content/20" />
              </div>
              <h3 className="text-2xl font-black text-base-content mb-2 tracking-tight">No roles found</h3>
              <p className="text-base-content/50 max-w-xs mx-auto font-bold uppercase tracking-widest text-[10px]">
                Try adjusting your search or create a new role
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto glass-panel rounded-2xl border border-base-content/5">
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-base-content/5">
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5 px-6">Display Name</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Name</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Scope</th>
                    <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5">Type</th>
                    {(can.roles.update() || can.roles.delete()) && (
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-5 px-6 text-right">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr key={role.id} className="hover:bg-base-content/5 transition-colors border-b border-base-content/5 last:border-0 group">
                      <td className="py-4 px-6">
                        <div className="font-black text-base-content group-hover:text-primary transition-colors tracking-tight">
                          {role.displayName}
                          {role.isDefault && (
                            <span className="ml-2 badge badge-primary badge-outline text-[8px] h-4 py-0 px-1 font-black uppercase">Default</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4">
                        <code className="text-[10px] font-black text-base-content/30 uppercase tracking-tight bg-base-content/5 px-2 py-1 rounded-lg">
                          {role.name}
                        </code>
                      </td>
                      <td className="py-4">
                        <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-xl border ${role.scope === 'platform' ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' :
                          role.scope === 'account' ? 'bg-primary/10 text-primary border-primary/20' :
                            'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                          }`}>
                          {role.scope?.replace('_', ' ') || 'Sub Account'}
                        </span>
                      </td>
                      <td className="py-4">
                        <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-xl border ${role.isSystem ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-blue-500/10 text-blue-500 border-blue-500/20'}`}>
                          {role.isSystem ? 'System' : 'Custom'}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-right">
                        {!role.isSystem && (
                          <div className="flex items-center justify-end gap-1">
                            {can.roles.update() && (
                              <button
                                onClick={() => navigate(`/roles/${role.id}/edit`)}
                                className="p-2 hover:bg-primary/10 hover:text-primary rounded-lg transition-all text-base-content/30"
                                title="Edit Role"
                              >
                                <Edit2 size={18} />
                              </button>
                            )}

                            {can.roles.delete() && !role.isSystem && (
                              <button
                                onClick={() => handleOpenDelete(role)}
                                className="p-2 hover:bg-error/10 hover:text-error rounded-lg transition-all text-base-content/30"
                                title="Delete Role"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pagination.totalPages > 1 && (
                <div className="px-4">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={(page) => setPagination(p => ({ ...p, page }))}
                    loading={loading}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>


      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Role"
      >
        <div className="mb-6">
          <p className="text-base-content/60 text-sm leading-relaxed">
            Are you sure you want to delete the role <strong className="text-base-content">{selectedRole?.displayName}</strong>?
            This action cannot be undone. All users assigned to this role will lose their current access levels immediately.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
            disabled={submitting}
          >
            {submitting ? 'Deleting...' : 'Delete Role'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Roles;